import { useSeo } from "@/lib/seo";
import React, { useEffect, useMemo, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BarChart3, Eye, MessageSquare, Target, Sparkles, AlertTriangle, Save, Crown, LayoutDashboard, Users, Kanban, Building2, CalendarDays, LineChart, Bot, Inbox, Calculator, UserPlus, FileSignature, BookUser, Search, Radar, Upload, ChevronLeft, ChevronRight, Menu, X, Rss, Briefcase, Shield, ShieldAlert, MapPin, PlusSquare, Home as HomeIcon, Globe, Gavel, ScanSearch, User } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import CrmLeads from "./crm/CrmLeads";
import CrmPipeline from "./crm/CrmPipeline";
import CrmProperties from "./crm/CrmProperties";
import CrmCalendar from "./crm/CrmCalendar";
import CrmAssistant from "./crm/CrmAssistant";
import AiAssistantDrawer from "./crm/AiAssistantDrawer";
import CrmAnalytics from "./crm/CrmAnalytics";
import CrmEmailParsing from "./crm/CrmEmailParsing";
import CrmValuation from "./crm/CrmValuation";
import CrmTeam from "./crm/CrmTeam";
import CrmContracts from "./crm/CrmContracts";
import CrmContacts from "./crm/CrmContacts";
import AcquisizionePrivati from "./crm/AcquisizionePrivati";
import NuoviAnnunciPrivati from "./crm/NuoviAnnunciPrivati";
import CrmImport from "./crm/CrmImport";
import CrmDeals from "./crm/CrmDeals";
import CrmPrivacy from "./crm/CrmPrivacy";
import CrmOwnerLeads from "./crm/CrmOwnerLeads";
import CrmZoneOperative from "./crm/CrmZoneOperative";
import CrmNewProperty from "./crm/CrmNewProperty";
import CrmWebsite from "./crm/CrmWebsite";
import AgencyOffices from "./crm/AgencyOffices";
import CrmAste from "./crm/CrmAste";
import CrmNextSeller from "./crm/CrmNextSeller";
import CrmAml from "./crm/CrmAml";
import SocialBuyerRadar from "@/components/SocialBuyerRadar";
import { OnboardingProvider, OnboardingKick, OnboardingLauncher } from "../onboarding/Onboarding";

const TABS = [
    { id: "panoramica", label: "Panoramica", icon: LayoutDashboard },
    { id: "crm", label: "CRM", icon: Users },
    { id: "pipeline", label: "Pipeline", icon: Kanban },
    { id: "calendario", label: "Calendario", icon: CalendarDays },
    { id: "nuovo-annuncio", label: "Nuovo Annuncio", icon: PlusSquare },
    { id: "immobili", label: "Immobili", icon: Building2 },
    { id: "contatti", label: "Contatti", icon: BookUser },
    { id: "acquisizione", label: "Acquisizione Privati", icon: Search },
    { id: "nuovi-privati", label: "Nuovi Privati", icon: Radar },
    { id: "aste", label: "Radar Aste", icon: Gavel },
    { id: "next-seller", label: "Next Seller", icon: Target },
    { id: "social-radar", label: "Radar Buyer Social", icon: ScanSearch },
    { id: "aml", label: "Antiriciclaggio (AML)", icon: ShieldAlert },
    { id: "email", label: "Email Parsing", icon: Inbox },
    { id: "valutazione", label: "Valutazione", icon: Calculator },
    { id: "contratti", label: "Contratti", icon: FileSignature },
    { id: "pratiche", label: "Pratiche", icon: Briefcase },
    { id: "lead-proprietari", label: "Lead Proprietari", icon: UserPlus },
    { id: "zone-operative", label: "Zone Operative", icon: MapPin },
    { id: "sito-web", label: "Sito Web", icon: Globe },
    { id: "privacy", label: "Privacy Policy", icon: Shield },
    { id: "portali", label: "Attiva i portali", icon: Rss },
    { id: "uffici", label: "Centro Operativo", icon: Building2 },
    { id: "importa", label: "Importa Dati", icon: Upload },
    { id: "team", label: "Team", icon: UserPlus, adminOnly: true },
    { id: "analytics", label: "Analytics", icon: LineChart },
    { id: "ai", label: "AI Assistant", icon: Bot },
];

export default function AnalyticsPro() {
    useSeo({ title: "VD Manager | VendiDirect", description: "CRM immobiliare per agenzie: lead, pipeline, calendario e firma digitale.", noindex: true });
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("panoramica");
    const location = useLocation();
    // Deep-link: /analytics-pro?tab=crm — usato da briefing/notifiche
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const requested = params.get("tab");
        if (requested && TABS.some((t) => t.id === requested)) {
            setTab(requested);
        }
    }, [location.search]);
    const [thresholds, setThresholds] = useState({ min_views_30d: 50, min_contacts_30d: 1, max_days_inactive: 30 });
    const [savingT, setSavingT] = useState(false);
    const [plNewCount, setPlNewCount] = useState(0);
    const visibleTabs = useMemo(() => TABS.filter((t) => !(t.adminOnly && user?.is_collaborator)), [user]);
    const [plBannerDismissed, setPlBannerDismissed] = useState(false);
    // Sidebar: sticky + independent scroll con altezza sincronizzata al viewport.
    // Uso callback ref: si attiva quando l'elemento monta/smonta (anche dopo i loading gate).
    const asideCbRef = React.useCallback((el) => {
        if (!el) return;
        const set = () => {
            el.style.setProperty("height", `${window.innerHeight - 32}px`, "important");
        };
        set();
        window.addEventListener("resize", set);
        el.__vdSidebarResize = set;
    }, []);

    // Sidebar state (persistente in localStorage)
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        try { return localStorage.getItem("vd_sidebar_open") === "1"; } catch { return false; }
    });
    const toggleSidebar = () => {
        setSidebarOpen((v) => {
            const next = !v;
            try { localStorage.setItem("vd_sidebar_open", next ? "1" : "0"); } catch (_e) { /* ignore */ }
            return next;
        });
    };
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
    const [urgentCount, setUrgentCount] = useState(0);
    const currentTab = visibleTabs.find((t) => t.id === tab) || visibleTabs[0];
    const isAgency = !!(user && (user.role === "agenzia" || user.is_admin || user.is_collaborator));

    // Poll the AI urgent counter every 60s so the FAB badge stays fresh.
    useEffect(() => {
        if (!user || !isAgency) return undefined;
        let alive = true;
        const fetchCount = () => {
            api.get("/crm/assistant/urgent-count")
                .then((r) => { if (alive) setUrgentCount(r.data?.total || 0); })
                .catch(() => { if (alive) setUrgentCount(0); });
        };
        fetchCount();
        const t = setInterval(fetchCount, 60000);
        return () => { alive = false; clearInterval(t); };
    }, [user, isAgency]);

    useEffect(() => {
        if (!user) return;
        api.get("/crm/private-listings/summary")
            .then((r) => setPlNewCount(r.data.new_count || 0))
            .catch(() => {});
    }, [user]);

    useEffect(() => {
        if (!user) return;
        api.get("/analytics-pro/dashboard")
            .then((r) => {
                setData(r.data);
                if (r.data.thresholds && Object.keys(r.data.thresholds).length) setThresholds({ ...thresholds, ...r.data.thresholds });
            })
            .catch(() => setData({ error: true }))
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, [user]);

    const saveThresholds = async () => {
        setSavingT(true);
        try { await api.put("/analytics-pro/thresholds", thresholds); }
        catch (e) { console.error(e); }
        finally { setSavingT(false); }
    };

    if (!user) return <div className="pt-32 text-center text-ink-400">Effettua l'accesso.</div>;
    if (loading) return <div className="pt-32 text-center text-ink-400">Caricamento VD Manager...</div>;

    if (user.role !== "agenzia" && !user.is_admin && !user.is_collaborator) {
        return (
            <div className="pt-32 max-w-2xl mx-auto px-6 text-center">
                <Sparkles size={36} className="text-purple-300 mx-auto mb-4" />
                <h1 className="font-serif text-3xl text-ink-50">VD Manager</h1>
                <p className="text-ink-400 mt-3 mb-8">Riservato agli account agenzia. Passa a un piano Agenzia per accedere.</p>
                <Link to="/prezzi" className="cta-gold px-6 py-3 text-sm">Vedi piani agenzia</Link>
            </div>
        );
    }

    // Agenzia Pro (€69) NON ha VD Manager: mostra upsell + link al feed config.
    if (user.plan_id === "premium" && !user.is_admin && !user.is_collaborator) {
        return (
            <div data-testid="analytics-pro-agpro-upsell" className="pt-32 max-w-3xl mx-auto px-6 text-center">
                <Sparkles size={36} className="text-purple-300 mx-auto mb-4" />
                <h1 className="font-serif text-3xl text-ink-50">VD Manager riservato al piano Agenzia + VD Manager</h1>
                <div className="mt-3 mb-2 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-purple-200 border border-purple-700/40 bg-purple-900/30 px-3 py-1">
                    Attualmente sul piano Agenzia Pro · €69/mese
                </div>
                <p className="text-ink-400 mt-4 mb-8 leading-relaxed">
                    Il tuo piano include sincronizzazione feed XML/CSV, profilo agenzia verificato, posizionamento prioritario e statistiche avanzate.
                    <br /><br />
                    Il <strong className="text-purple-200">VD Manager</strong> (CRM completo, Pipeline, Pratiche, Matching, Lead proprietari, AI Assistant) è incluso solo nel piano <strong>Agenzia + VD Manager</strong> a €129/mese.
                </p>
                <div className="flex gap-3 justify-center flex-wrap">
                    <Link to="/agenzia/feed" data-testid="agpro-configure-feed-cta" className="cta-outline px-6 py-3 text-sm">
                        Configura il tuo Feed →
                    </Link>
                    <Link to="/prezzi" data-testid="agpro-upgrade-cta" className="cta-gold px-6 py-3 text-sm">
                        Upgrade a Agenzia + VD Manager (€129) →
                    </Link>
                </div>
            </div>
        );
    }

    // VD Manager è incluso nel piano Agenzia + VD Manager: il backend risponde 402 se il piano non è attivo.
    if (data?.error) {
        return (
            <div data-testid="analytics-pro-paywall" className="pt-32 max-w-2xl mx-auto px-6 text-center">
                <Sparkles size={36} className="text-purple-300 mx-auto mb-4" />
                <h1 className="font-serif text-3xl text-ink-50">VD Manager non attivo</h1>
                <div className="mt-3 mb-2 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-purple-200 border border-purple-700/40 bg-purple-900/30 px-3 py-1">
                    Incluso nel piano Agenzia + VD Manager
                </div>
                <p className="text-ink-400 mt-4 mb-8">
                    VD Manager è incluso nel piano Agenzia + VD Manager (€129/mese). Attivando il piano sblocchi:
                    <br /><br />
                    CRM completo (lead, pipeline, calendario, team) · Acquisizione Privati · Nuovi Annunci Privati · AI Assistant · campagne email/WhatsApp · dashboard KPI avanzata · distribuzione feed portali · report PDF.
                </p>
                <Link to="/prezzi" data-testid="analytics-pro-subscribe-cta" className="cta-gold px-6 py-3 text-sm">
                    Attiva il piano Agenzia + VD Manager →
                </Link>
                <p className="text-[11px] text-ink-400 mt-4">€129/mese, tutto incluso. Cancellabile in ogni momento.</p>
            </div>
        );
    }

    const k = data?.kpi || {};
    const alerts = [];
    if (k.contacts_received === 0 && k.total_listings > 0) alerts.push({ level: "warning", text: "Nessun contatto ricevuto. Migliora foto e descrizione." });
    if (k.avg_views_per_listing < (thresholds.min_views_30d || 0)) alerts.push({ level: "info", text: `Media views per annuncio sotto la soglia (${k.avg_views_per_listing} < ${thresholds.min_views_30d}).` });

    const maxViews = Math.max(1, ...(data?.breakdown || []).map((b) => b.views));

    return (
        <OnboardingProvider onNavigateTab={setTab}>
        <OnboardingKick tab={tab} />
        <div data-testid="analytics-pro-page" data-vd-manager="true" className="pt-32 pb-24 px-6 lg:px-12 2xl:px-20">
            <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-2"><Sparkles size={16} className="text-purple-300" /><span className="label-eyebrow text-purple-300">VD Manager · Agenzia</span></div>
                    <h1 className="font-serif text-4xl text-ink-50 mt-2">Centro operativo</h1>
                    {user.is_collaborator && (
                        <div data-testid="collab-badge" className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase text-gold border border-gold/30 bg-gold/5 px-2.5 py-1">
                            <Users size={11} /> Collaboratore · {user.collab_agency_name}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Link
                        to="/profilo"
                        data-testid="vd-profile-link"
                        aria-label="Il mio profilo"
                        title="Il mio profilo"
                        className="vd-profile-quick-link"
                    >
                        <User size={18} />
                    </Link>
                    <OnboardingLauncher tab={tab} />
                    <Link to="/dashboard" className="cta-outline px-4 py-2 text-sm">← Dashboard standard</Link>
                </div>
            </div>

            {/* Notifica discreta nuovi annunci privati */}
            {plNewCount > 0 && !plBannerDismissed && tab !== "nuovi-privati" && (
                <div data-testid="pl-crm-notification" className="mb-6 p-3 border border-gold/30 bg-gold/5 text-sm text-ink-50 flex items-center gap-3">
                    <Radar size={15} className="text-gold shrink-0" />
                    <span>{plNewCount === 1 ? "È disponibile 1 nuovo annuncio privato." : `Sono disponibili ${plNewCount} nuovi annunci privati.`}</span>
                    <button data-testid="pl-notification-open" onClick={() => setTab("nuovi-privati")} className="text-gold hover:underline text-xs uppercase tracking-wider">Vedi ora →</button>
                    <button data-testid="pl-notification-dismiss" onClick={() => setPlBannerDismissed(true)} className="ml-auto text-ink-500 hover:text-ink-100 text-lg leading-none">×</button>
                </div>
            )}

            {/* Mobile: dropdown menu dall'alto */}
            <div className="md:hidden mb-4">
                <button
                    data-testid="sidebar-mobile-toggle"
                    onClick={() => setMobileMenuOpen((v) => !v)}
                    className="vd-sidebar-toggle-mobile w-full flex items-center justify-between px-4 py-3 text-ink-100"
                >
                    <span className="flex items-center gap-2 text-sm">
                        {currentTab?.icon && React.createElement(currentTab.icon, { size: 15, className: "text-gold" })}
                        {currentTab?.label}
                    </span>
                    {mobileMenuOpen ? <X size={16} /> : <Menu size={16} />}
                </button>
                {mobileMenuOpen && (
                    <div data-testid="sidebar-mobile-menu" className="vd-sidebar mt-2 max-h-[70vh] overflow-y-auto p-2">
                        {visibleTabs.map((t) => {
                            const Icon = t.icon;
                            const active = tab === t.id;
                            return t.href ? (
                                <Link
                                    key={t.id}
                                    to={t.href}
                                    data-testid={`tab-${t.id}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="vd-nav-item w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors"
                                >
                                    <Icon size={15} /> {t.label}
                                </Link>
                            ) : (
                                <button
                                    key={t.id}
                                    data-testid={`tab-${t.id}`}
                                    onClick={() => { setTab(t.id); setMobileMenuOpen(false); }}
                                    className={`vd-nav-item ${active ? "vd-nav-item-active" : ""} w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors`}
                                >
                                    <Icon size={15} /> {t.label}
                                    {t.id === "nuovi-privati" && plNewCount > 0 && (
                                        <span data-testid="pl-tab-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-ink-950 text-[10px] font-bold flex items-center justify-center">{plNewCount}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Desktop: sidebar collapsible + main */}
            <div className="flex gap-6 items-start">
                <aside
                    ref={asideCbRef}
                    data-testid="analytics-pro-tabs"
                    className={`vd-sidebar vd-sidebar-sticky hidden md:flex flex-col shrink-0 transition-[width] duration-200 ease-out ${sidebarOpen ? "w-56" : "w-14"}`}
                >
                    <button
                        data-testid="sidebar-toggle"
                        onClick={toggleSidebar}
                        title={sidebarOpen ? "Comprimi sidebar" : "Espandi sidebar"}
                        className={`vd-sidebar-toggle shrink-0 flex items-center gap-2 px-3 py-3 text-xs uppercase tracking-wider ${sidebarOpen ? "justify-between" : "justify-center"}`}
                    >
                        {sidebarOpen && <span>Menu</span>}
                        {sidebarOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <nav className="flex-1 min-h-0 overflow-y-auto py-2 px-2 sidebar-scroll">
                        {visibleTabs.map((t) => {
                            const Icon = t.icon;
                            const active = tab === t.id;
                            return t.href ? (
                                <Link
                                    key={t.id}
                                    to={t.href}
                                    data-testid={`tab-${t.id}`}
                                    title={sidebarOpen ? undefined : t.label}
                                    className={`vd-nav-item w-full flex items-center ${sidebarOpen ? "gap-3 px-4" : "justify-center px-0"} py-3 text-sm transition-colors`}
                                >
                                    <Icon size={15} className="shrink-0" />
                                    {sidebarOpen && <span className="truncate">{t.label}</span>}
                                </Link>
                            ) : (
                                <button
                                    key={t.id}
                                    data-testid={`tab-${t.id}`}
                                    onClick={() => {
                                        setTab(t.id);
                                        const params = new URLSearchParams(location.search);
                                        params.set("tab", t.id);
                                        window.history.replaceState(null, "", `${location.pathname}?${params.toString()}`);
                                    }}
                                    title={sidebarOpen ? undefined : t.label}
                                    className={`vd-nav-item ${active ? "vd-nav-item-active" : ""} w-full flex items-center ${sidebarOpen ? "gap-3 px-4" : "justify-center px-0"} py-3 text-sm transition-colors`}
                                >
                                    <Icon size={15} className="shrink-0" />
                                    {sidebarOpen && <span className="truncate">{t.label}</span>}
                                    {t.id === "nuovi-privati" && plNewCount > 0 && (
                                        <span data-testid="pl-tab-badge" className={`${sidebarOpen ? "ml-auto" : "absolute translate-x-4 -translate-y-3"} min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-ink-950 text-[10px] font-bold flex items-center justify-center`}>{plNewCount}</span>
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                </aside>

                <main className="flex-1 min-w-0">

            {tab === "crm" && <CrmLeads />}
            {tab === "pipeline" && <CrmPipeline onOpenAiAssistant={() => setAiDrawerOpen(true)} />}
            {tab === "calendario" && <CrmCalendar />}
            {tab === "immobili" && <CrmProperties />}
            {tab === "contatti" && <CrmContacts />}
            {tab === "acquisizione" && <AcquisizionePrivati />}
            {tab === "nuovi-privati" && <NuoviAnnunciPrivati onNewCountChange={setPlNewCount} />}
            {tab === "aste" && <CrmAste onGoToZones={() => setTab("zone-operative")} />}
            {tab === "next-seller" && <CrmNextSeller onGoToZones={() => setTab("zone-operative")} />}
            {tab === "social-radar" && (
                <div data-testid="crm-social-radar-page">
                    <div className="mb-6">
                        <span className="label-eyebrow text-gold">VD Manager · Acquisizione</span>
                        <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-2"><ScanSearch size={22} className="text-gold" /> Radar Buyer Social</h2>
                        <p className="text-ink-400 text-sm mt-1">L'IA intercetta chi scrive &quot;cerco casa&quot; nei gruppi Facebook e su Instagram nelle tue città e lo incrocia con i tuoi immobili.</p>
                    </div>
                    <SocialBuyerRadar />
                </div>
            )}
            {tab === "aml" && <CrmAml />}
            {tab === "email" && <CrmEmailParsing />}
            {tab === "valutazione" && <CrmValuation />}
            {tab === "team" && <CrmTeam />}
            {tab === "contratti" && <CrmContracts />}
            {tab === "pratiche" && <CrmDeals />}
            {tab === "lead-proprietari" && <CrmOwnerLeads />}
            {tab === "zone-operative" && <CrmZoneOperative />}
            {tab === "nuovo-annuncio" && <CrmNewProperty onGoToProperties={() => setTab("immobili")} />}
            {tab === "sito-web" && <CrmWebsite />}
            {tab === "privacy" && <CrmPrivacy />}
            {tab === "portali" && (
                <div className="relative min-h-[70vh]" data-testid="portals-page">
                    <div data-testid="portals-content">
                        <div className="mb-6">
                            <span className="label-eyebrow text-gold">VD Manager · Distribuzione</span>
                            <h2 className="font-serif text-3xl text-ink-50 mt-2">Attiva i portali</h2>
                            <p className="text-ink-400 text-sm mt-2">Distribuisci i tuoi annunci sui portali immobiliari italiani tramite feed XML dedicati per agenzia.</p>
                        </div>
                        <ExternalFeedsCard />
                    </div>
                </div>
            )}
            {tab === "uffici" && <AgencyOffices />}
            {tab === "importa" && <CrmImport />}
            {tab === "analytics" && <CrmAnalytics />}
            {tab === "ai" && <CrmAssistant />}

            {tab === "panoramica" && (<>
            <SocialBuyerRadar limit={4} onSeeAll={() => setTab("social-radar")} />
            {alerts.length > 0 && (
                <div data-testid="alerts" className="mb-10 space-y-2">
                    {alerts.map((a, i) => (
                        <div key={i} className={`p-4 border text-sm flex items-start gap-3 ${a.level === "warning" ? "border-amber-700/40 bg-amber-900/20 text-amber-200" : "border-blue-700/40 bg-blue-900/10 text-blue-200"}`}>
                            <AlertTriangle size={16} className="mt-0.5" /> {a.text}
                        </div>
                    ))}
                </div>
            )}

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
                <KpiCard label="Annunci attivi" value={k.active} icon={<BarChart3 />} />
                <KpiCard label="Views totali" value={k.total_views} icon={<Eye />} />
                <KpiCard label="Media views/annuncio" value={k.avg_views_per_listing} icon={<Eye />} />
                <KpiCard label="Contatti ricevuti" value={k.contacts_received} icon={<MessageSquare />} />
                <KpiCard label="Conversion %" value={`${k.conversion_rate_pct}%`} icon={<Target />} highlight />
            </div>

            {/* Heatmap per-property */}
            <div className="mb-16 border border-white/10 p-6 bg-ink-900/40">
                <h2 className="font-serif text-2xl text-ink-50 mb-2">Performance per annuncio</h2>
                <p className="text-xs text-ink-400 mb-6">Heatmap di engagement. Gli annunci più "caldi" sono quelli con più views.</p>
                <div className="space-y-1">
                    {(data?.breakdown || []).map((b) => {
                        const pct = Math.round((b.views / maxViews) * 100);
                        const heat = pct > 80 ? "bg-gold" : pct > 50 ? "bg-purple-500" : pct > 20 ? "bg-blue-500" : "bg-ink-700";
                        return (
                            <div key={b.id} data-testid={`row-${b.id}`} className="grid grid-cols-12 gap-3 items-center py-2 px-3 hover:bg-white/5 text-sm">
                                <div className="col-span-4 text-ink-50 truncate font-serif">{b.title}</div>
                                <div className="col-span-2 text-xs text-ink-400">{b.city}</div>
                                <div className="col-span-1 text-xs text-price">{formatPrice(b.price)}</div>
                                <div className="col-span-4 relative h-3 bg-ink-950 overflow-hidden">
                                    <div className={`absolute inset-y-0 left-0 ${heat} transition-all`} style={{ width: `${pct}%` }} />
                                    <span className="absolute right-2 top-0 text-[10px] text-ink-100 leading-3">{b.views}v</span>
                                </div>
                                <div className="col-span-1 text-right text-xs">
                                    <span className={`px-2 py-0.5 ${b.status === "sold" || b.status === "rented" ? "bg-green-900/30 text-green-300" : b.status === "removed" ? "bg-red-900/30 text-red-300" : "bg-ink-900 text-ink-400"}`}>
                                        {b.status}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                    {(data?.breakdown || []).length === 0 && <p className="text-ink-400 text-sm p-6">Nessun annuncio ancora. Pubblicane uno!</p>}
                </div>
            </div>

            {/* Thresholds */}
            <div className="border border-white/10 p-6 bg-ink-900/40">
                <h2 className="font-serif text-2xl text-ink-50 mb-2">Soglie alert personalizzate</h2>
                <p className="text-xs text-ink-400 mb-6">Ricevi una notifica quando le metriche scendono sotto le soglie che imposti.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        { k: "min_views_30d", label: "Min views/annuncio (30gg)" },
                        { k: "min_contacts_30d", label: "Min contatti/annuncio (30gg)" },
                        { k: "max_days_inactive", label: "Max giorni inattività" },
                    ].map((t) => (
                        <div key={t.k}>
                            <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">{t.label}</label>
                            <input
                                data-testid={`threshold-${t.k}`}
                                type="number"
                                value={thresholds[t.k] ?? 0}
                                onChange={(e) => setThresholds({ ...thresholds, [t.k]: parseInt(e.target.value || "0") })}
                                className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none"
                            />
                        </div>
                    ))}
                </div>
                <button data-testid="save-thresholds" onClick={saveThresholds} disabled={savingT} className="mt-6 cta-outline px-5 py-2.5 text-sm flex items-center gap-2"><Save size={14} /> {savingT ? "Salvataggio..." : "Salva soglie"}</button>
            </div>
            </>)}
                </main>
            </div>

            {/* Floating AI Assistant trigger — circular FAB stacked vertically ABOVE the support Chatbot.
                Support Chatbot sits at bottom-6 right-6 (w-14 h-14) inside VD Manager → AI FAB at bottom-24 right-6 (~16px gap). */}
            {tab !== "ai" && tab !== "acquisizione" && (
                <button
                    data-testid="ai-assistant-fab"
                    onClick={() => setAiDrawerOpen(true)}
                    aria-label="Apri AI Assistant"
                    title="AI Assistant"
                    className="fixed bottom-24 right-6 z-[70] group w-14 h-14 rounded-full flex items-center justify-center text-white transition-all hover:scale-105 active:scale-95"
                    style={{
                        background: "linear-gradient(135deg, #007BFF 0%, #4F46E5 100%)",
                        boxShadow: "0 8px 24px rgba(0, 123, 255, 0.45), 0 2px 6px rgba(0,0,0,0.25)",
                    }}
                >
                    <Sparkles size={22} className="text-white drop-shadow-sm" />
                    {urgentCount > 0 ? (
                        <span
                            data-testid="ai-fab-badge"
                            aria-label={`${urgentCount} elementi urgenti`}
                            className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 rounded-full bg-[#FF4D4D] text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-[#111111] shadow-md animate-pulse"
                        >
                            {urgentCount > 99 ? "99+" : urgentCount}
                        </span>
                    ) : (
                        <span
                            aria-hidden="true"
                            className="absolute top-0 right-0 w-3 h-3 rounded-full bg-[#4ADE80] ring-2 ring-[#111111]"
                        />
                    )}
                </button>
            )}

            {tab !== "acquisizione" && (
                <AiAssistantDrawer
                    open={aiDrawerOpen}
                    onClose={() => setAiDrawerOpen(false)}
                    onNavigateTab={(t) => setTab(t)}
                />
            )}
        </div>
        </OnboardingProvider>
    );
}

function KpiCard({ label, value, icon, highlight }) {
    return (
        <div className={`p-5 border ${highlight ? "border-blue-400/40 bg-blue-900/10" : "border-white/10 bg-ink-900/40"}`}>
            <div className={`flex items-center justify-between mb-3 ${highlight ? "text-blue-300" : "text-ink-400"}`}>
                {React.cloneElement(icon, { size: 16 })}
                <Crown size={11} className="text-gold" />
            </div>
            <div className="font-serif text-3xl text-ink-50">{value ?? "—"}</div>
            <div className="text-[11px] text-ink-400 mt-1 uppercase tracking-wider">{label}</div>
        </div>
    );
}

function ExternalFeedsCard() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(null);
    const [validation, setValidation] = useState({}); // { portal_id: result }
    const [validating, setValidating] = useState(null);

    const load = async () => {
        try { const r = await api.get("/agency/feeds/me"); setCfg(r.data); } catch (e) { console.error(e); }
    };
    useEffect(() => { load(); }, []);

    const enable = async (portal) => {
        setBusy(portal);
        try { await api.post("/agency/feeds/enable", { portal }); await load(); }
        catch (e) { alert(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(null); }
    };
    const enableAllStandard = async () => {
        setBusy("__bulk__");
        try {
            const r = await api.post("/agency/feeds/enable-bulk", { group: "standard" });
            await load();
            const n = r.data?.activated_count ?? 0;
            alert(n > 0 ? `Attivati ${n} nuovi portali standard.` : "Tutti i portali standard erano già attivi.");
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore attivazione bulk");
        } finally { setBusy(null); }
    };
    const disableAllStandard = async () => {
        if (!window.confirm("Disattivare TUTTI i 24 portali italiani standard? La distribuzione XML verso questi portali verrà interrotta immediatamente.")) return;
        setBusy("__bulk_off__");
        try {
            const r = await api.post("/agency/feeds/disable-bulk", { group: "standard" });
            await load();
            const n = r.data?.deactivated_count ?? 0;
            alert(n > 0 ? `Disattivati ${n} portali standard.` : "Nessun portale standard era attivo.");
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore disattivazione bulk");
        } finally { setBusy(null); }
    };
    const disable = async (portal) => {
        setBusy(portal);
        try { await api.post("/agency/feeds/disable", { portal }); await load(); }
        finally { setBusy(null); }
    };
    const copy = (url) => { navigator.clipboard.writeText(url); alert("URL copiato!"); };

    const verify = async (portal) => {
        setValidating(portal);
        try {
            const r = await api.get(`/agency/feeds/${portal}/validate`);
            setValidation((v) => ({ ...v, [portal]: r.data }));
        } catch (e) {
            setValidation((v) => ({ ...v, [portal]: { ok: false, error: e?.response?.data?.detail || "Errore" } }));
        } finally { setValidating(null); }
    };

    const PORTALS = [
        { id: "idealista", name: "Idealista", color: "text-green-400", url: "https://www.idealista.it/pro/", info: "Idealista NON pulla automaticamente URL pubblici. Per attivare l'ingestion devi (a) avere un account idealista/tools a pagamento e contattare il loro supporto per abilitare l'import del feed XML, oppure (b) iscriverti al loro programma partner B2B (api.idealista.com/access-request). Il feed sotto è tecnicamente valido e pronto da fornire al referente." },
        { id: "casait", name: "Casa.it", color: "text-orange-400", url: "https://www.casa.it/agenzie", info: "Accedi al tuo account Casa.it Business → contatta il tuo account manager fornendogli l'URL feed. Loro lo configureranno lato portale." },
        { id: "immobiliare", name: "Immobiliare.it", color: "text-red-400", url: "https://www.immobiliare.it/vetrinapro/", info: "Immobiliare.it Vetrina Pro permette l'import XML, ma l'abilitazione del feed partner richiede contatto col supporto commerciale." },
        { id: "subito", name: "Subito.it", color: "text-sky-400", url: "https://impresapiu.subito.it/", info: "Subito.it Impresa+ importa annunci via feed XML tramite gestionali partner integrati: fornisci l'URL feed al supporto Impresa+ o al tuo referente commerciale per l'abilitazione dell'import." },
        // Portali IT aggiuntivi (feed XML standard italiano)
        { id: "allaricerca", name: "Allaricerca.it", color: "text-emerald-300", url: "https://www.allaricerca.it/", info: "Portale immobiliare italiano. Fornisci l'URL feed al referente commerciale per abilitare l'import automatico." },
        { id: "annuncirepubblica", name: "Annunci Repubblica", color: "text-red-300", url: "https://annunci.repubblica.it/", info: "Sezione annunci de La Repubblica. Contatta il supporto commerciale con l'URL feed." },
        { id: "babacasa", name: "Babacasa.com", color: "text-pink-300", url: "https://www.babacasa.com/", info: "Portale immobiliare italiano. Fornisci l'URL feed al referente commerciale per abilitare l'import." },
        { id: "bachecacase", name: "Bachecacase.com", color: "text-amber-300", url: "https://www.bachecacase.com/", info: "Bacheca annunci case in Italia. Invia l'URL feed al referente per l'attivazione." },
        { id: "bakeca", name: "Bakeca.it", color: "text-blue-300", url: "https://www.bakeca.it/", info: "Bakeca Immobiliare. Fornisci l'URL feed al referente commerciale per l'import automatico." },
        { id: "bancadellecase", name: "Bancadellecase.it", color: "text-yellow-300", url: "https://www.bancadellecase.it/", info: "Portale immobiliare italiano. Fornisci l'URL feed al supporto commerciale." },
        { id: "bilocale", name: "Bilocale.it", color: "text-purple-300", url: "https://www.bilocale.it/", info: "Portale specializzato in bilocali. Contatta il supporto per l'abilitazione del feed." },
        { id: "casamontagna", name: "Casamontagna.it", color: "text-teal-300", url: "https://www.casamontagna.it/", info: "Portale case in montagna. Invia l'URL feed al referente commerciale." },
        { id: "cercacasa", name: "Cercacasa.it", color: "text-indigo-300", url: "https://www.cercacasa.it/", info: "Portale immobiliare italiano. Fornisci l'URL feed al supporto." },
        { id: "cercasicasa", name: "Cercasicasa.it", color: "text-lime-300", url: "https://www.cercasicasa.it/", info: "Portale immobiliare italiano. Contatta il referente con l'URL feed." },
        { id: "cheannunci", name: "Cheannunci.it", color: "text-orange-300", url: "https://www.cheannunci.it/", info: "Portale annunci generalisti sezione casa. Fornisci l'URL feed al referente." },
        { id: "easyavvisi", name: "Easyavvisi.it", color: "text-cyan-300", url: "https://www.easyavvisi.it/", info: "Portale annunci immobiliari. Invia l'URL feed al supporto commerciale." },
        { id: "homilio", name: "Homilio.it", color: "text-rose-300", url: "https://www.homilio.it/", info: "Portale immobiliare italiano. Fornisci l'URL feed al referente." },
        { id: "ioaffitto", name: "Ioaffitto.it", color: "text-fuchsia-300", url: "https://www.ioaffitto.it/", info: "Portale specializzato in affitti. Contatta il supporto con l'URL feed." },
        { id: "kyero", name: "Kyero.com", color: "text-sky-300", url: "https://www.kyero.com/", info: "Portale immobiliare internazionale. Fornisci l'URL feed al partner desk." },
        { id: "offrocasa", name: "Offrocasa.com", color: "text-violet-300", url: "https://www.offrocasa.com/", info: "Portale immobiliare italiano. Invia l'URL feed al referente." },
        { id: "oikia", name: "Oikia.it", color: "text-green-300", url: "https://www.oikia.it/", info: "Portale immobiliare italiano. Contatta il supporto per l'abilitazione." },
        { id: "pcase", name: "Pcase.it", color: "text-red-300", url: "https://www.pcase.it/", info: "Portale immobiliare italiano. Fornisci l'URL feed al referente." },
        { id: "reesty", name: "Reesty.it", color: "text-emerald-300", url: "https://www.reesty.it/", info: "Portale immobiliare italiano. Contatta il supporto con l'URL feed." },
        { id: "trovacasa", name: "Trovacasa.net", color: "text-amber-300", url: "https://www.trovacasa.net/", info: "Portale immobiliare italiano. Fornisci l'URL feed al referente commerciale." },
        { id: "tuttocasa", name: "Tuttocasa.it", color: "text-blue-300", url: "https://www.tuttocasa.it/", info: "Portale immobiliare italiano. Invia l'URL feed al supporto." },
        { id: "vivoqui", name: "Vivoqui.it", color: "text-pink-300", url: "https://www.vivoqui.it/", info: "Portale immobiliare italiano. Contatta il referente con l'URL feed." },
        { id: "wikicasa", name: "Wikicasa.it", color: "text-teal-300", url: "https://www.wikicasa.it/", info: "Portale immobiliare italiano. Fornisci l'URL feed al referente commerciale." },
        { id: "yescasa", name: "YesCasa.it", color: "text-yellow-300", url: "https://www.yescasa.it/", info: "Portale immobiliare italiano. Invia l'URL feed al supporto commerciale." },
    ];

    return (
        <div className="mb-12 border border-purple-700/30 p-6 bg-purple-900/5">
            <div className="flex items-center gap-3 mb-2">
                <Target size={20} className="text-purple-300" />
                <h2 className="font-serif text-2xl text-ink-50">Distribuzione multi-portale</h2>
            </div>
            <p className="text-xs text-ink-400 mb-3">Generiamo per ogni portale un feed XML privato (con token anti-enumerazione). I portali italiani non hanno un endpoint self-service: l&apos;URL va fornito al referente del portale che lo abilita manualmente.</p>
            <div data-testid="feeds-honest-disclaimer" className="mb-5 p-3 border border-amber-600/40 bg-amber-900/15 text-xs text-amber-100 flex items-start gap-2">
                <span className="text-amber-300">ⓘ</span>
                <span>Il pulsante <b>&quot;Verifica feed&quot;</b> conferma che il tuo XML viene servito correttamente. L&apos;effettiva pubblicazione sui portali dipende dall&apos;abilitazione lato Idealista / Casa.it / Immobiliare.it.</span>
            </div>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3 p-3 border border-gold/30 bg-gold/5">
                <div className="text-xs text-ink-100">
                    <div className="text-gold font-medium mb-0.5">Attivazione bulk</div>
                    Attiva o disattiva in un solo click i 24 portali italiani a formato standard.
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        data-testid="enable-all-standard"
                        onClick={enableAllStandard}
                        disabled={busy === "__bulk__" || busy === "__bulk_off__"}
                        className="cta-gold px-5 py-2 text-xs disabled:opacity-50"
                    >
                        {busy === "__bulk__" ? "Attivazione..." : "⚡ Attiva tutti i portali standard"}
                    </button>
                    <button
                        data-testid="disable-all-standard"
                        onClick={disableAllStandard}
                        disabled={busy === "__bulk__" || busy === "__bulk_off__"}
                        className="px-5 py-2 text-xs border border-red-500/40 text-red-300 hover:border-red-400 hover:bg-red-900/20 transition-colors disabled:opacity-50"
                    >
                        {busy === "__bulk_off__" ? "Disattivazione..." : "⨯ Disattiva tutti i portali standard"}
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {PORTALS.map((p) => {
                    const enabled = cfg?.portals_enabled?.includes(p.id);
                    const url = cfg?.feed_urls?.[p.id];
                    const val = validation[p.id];
                    return (
                        <div key={p.id} data-testid={`portal-${p.id}`} className={`p-5 border ${enabled ? "border-gold/40 bg-gold/5" : "border-white/10 bg-ink-900/40"}`}>
                            <div className={`font-serif text-xl ${p.color} mb-2`}>{p.name}</div>
                            {enabled ? (
                                <>
                                    <div className="text-[10px] text-green-300 uppercase tracking-wider mb-3">✓ Attivo</div>
                                    {url && (
                                        <>
                                            <div className="text-[10px] text-ink-400 uppercase tracking-wider mb-1">URL feed (privato)</div>
                                            <div className="font-mono text-[10px] text-ink-100 bg-black/40 p-2 break-all mb-2">{url}</div>
                                            <div className="flex gap-3 mb-3">
                                                <button data-testid={`copy-${p.id}`} onClick={() => copy(url)} className="text-xs text-gold hover:underline">📋 Copia URL</button>
                                                <button
                                                    data-testid={`verify-${p.id}`}
                                                    onClick={() => verify(p.id)}
                                                    disabled={validating === p.id}
                                                    className="text-xs text-purple-200 hover:underline disabled:opacity-50"
                                                >
                                                    {validating === p.id ? "Verifica..." : "✓ Verifica feed"}
                                                </button>
                                            </div>
                                            {val && (
                                                <div
                                                    data-testid={`verify-result-${p.id}`}
                                                    className={`mb-3 p-3 text-[11px] border ${val.ok ? "border-green-700/40 bg-green-900/15 text-green-100" : "border-red-700/40 bg-red-900/15 text-red-100"}`}
                                                >
                                                    {val.ok ? (
                                                        <>
                                                            <div className="font-medium mb-1">✓ Feed servito correttamente</div>
                                                            <div className="text-ink-100/80">
                                                                HTTP {val.http_status} · {val.elapsed_ms}ms · {val.size_bytes}B · {val.advertisements_count} annunci
                                                            </div>
                                                            {val.first_preview?.title && (
                                                                <div className="mt-1 text-ink-400">Primo annuncio: <span className="text-ink-100">{val.first_preview.title}</span> ({val.first_preview.city})</div>
                                                            )}
                                                            {val.ingestion_note && (
                                                                <div className="mt-2 pt-2 border-t border-green-700/30 text-[10px] text-amber-200">
                                                                    <b>Nota ingestion:</b> {val.ingestion_note}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="font-medium mb-1">⨯ Errore</div>
                                                            <div>{val.error || "Errore sconosciuto"}</div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <p className="text-[11px] text-ink-400 mt-1 mb-3">{p.info}</p>
                                    <a href={p.url} target="_blank" rel="noreferrer" data-testid={`portal-link-${p.id}`} className="text-[11px] text-gold hover:underline block mb-2">→ Apri {p.name} Pro</a>
                                    <button data-testid={`disable-${p.id}`} onClick={() => disable(p.id)} disabled={busy === p.id} className="text-xs px-3 py-1.5 border border-red-700/30 text-red-300 hover:bg-red-900/20 w-full">Disattiva</button>
                                </>
                            ) : (
                                <>
                                    <p className="text-[11px] text-ink-400 mb-4">{p.info}</p>
                                    <a href={p.url} target="_blank" rel="noreferrer" className="text-[11px] text-gold hover:underline block mb-3">→ Apri {p.name} Pro</a>
                                    <button data-testid={`enable-${p.id}`} onClick={() => enable(p.id)} disabled={busy === p.id} className="w-full cta-gold py-2 text-xs">{busy === p.id ? "..." : `Attiva ${p.name}`}</button>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
